import { useUpdateBrokerageTransactionMutation } from '@/store/transactionsApi';
import { useCreateTransactionTypeOverrideMutation } from '@/store/transactionsApi';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Snackbar,
  Stack,
  Typography,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';

// The Investment Activity grid rows expose a subset of transaction fields; the
// modal only needs the identifier, the (optional) account for rule scoping and
// the description used to build the rule criteria.
export interface MoveToCashFlowTransaction {
  WealthAccountTransactionId: number | string;
  WealthAccountId?: number | null;
  Description?: string | null;
}

interface MoveToCashFlowModalProps {
  open: boolean;
  onClose: () => void;
  transaction: MoveToCashFlowTransaction | null;
  onDone?: () => void;
}

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100vw - 32px)', sm: 520 },
  maxWidth: '100%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 3, sm: 4 },
  borderRadius: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 3,
};

const comparisonOperators = [
  { value: 1, defaultLabel: 'Is' },
  { value: 2, defaultLabel: 'Contains' },
  { value: 3, defaultLabel: 'Begins With' },
  { value: 4, defaultLabel: 'Ends With' },
];

const MoveToCashFlowModal: React.FC<MoveToCashFlowModalProps> = ({
  open,
  onClose,
  transaction,
  onDone,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mode, setMode] = useState<'moveOnly' | 'createRule'>('moveOnly');
  const [comparisonOperator, setComparisonOperator] = useState<number>(1);
  const [comparisonText, setComparisonText] = useState<string>('');
  const [ruleScope, setRuleScope] = useState<'sameAccount' | 'all'>('all');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  } | null>(null);

  const [updateBrokerageTransaction, { isLoading: isSaving }] =
    useUpdateBrokerageTransactionMutation();
  const [createTransactionTypeOverride] =
    useCreateTransactionTypeOverrideMutation();

  useEffect(() => {
    if (open && transaction) {
      setMode('moveOnly');
      setComparisonOperator(1);
      setComparisonText(transaction.Description || '');
      setRuleScope('all');
    }
    if (!open) setSnackbar(null);
  }, [open, transaction]);

  const handleSave = async () => {
    if (!transaction) {
      setSnackbar({
        open: true,
        message: t('transactions.missingTransaction', 'Missing transaction.'),
        severity: 'error',
      });
      return;
    }

    const transactionId = String(transaction.WealthAccountTransactionId);

    try {
      if (!isMobile && mode === 'createRule') {
        const wealthAccountId =
          ruleScope === 'sameAccount' && transaction.WealthAccountId
            ? transaction.WealthAccountId
            : null;

        const displayDescription =
          comparisonOperator === 1 ? transaction.Description : comparisonText;

        const ruleBody = {
          ComparisonOperator: comparisonOperator,
          DisplayDescription: displayDescription || '',
          // 1 = Cash Flow. This is the only differentiator from the forward
          // "Move to Investment Activity" rule (which uses TransactionTypeId 2).
          TransactionTypeId: 1,
          ...(wealthAccountId != null
            ? { WealthAccountId: wealthAccountId }
            : {}),
        };

        await createTransactionTypeOverride({ payload: ruleBody }).unwrap();
      }

      // Always move the current transaction now
      await updateBrokerageTransaction({
        transactionId,
        transaction: {
          WealthAccountTransactionId: transactionId,
          NewTransactionTypeId: 1,
        },
      }).unwrap();

      setSnackbar({
        open: true,
        message: t('transactions.saved', 'Saved.'),
        severity: 'success',
      });
      onDone?.();
      onClose();
    } catch (err) {
      console.error('Failed to process request:', err);
      setSnackbar({
        open: true,
        message: t(
          'transactions.actionFailed',
          'Action failed. Please try again.',
        ),
        severity: 'error',
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(null);
  };
  const hasWealthAccountId = Boolean(transaction?.WealthAccountId);
  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextMode = event.target.value;
    setMode(nextMode === 'createRule' ? 'createRule' : 'moveOnly');
  };
  const handleRuleScopeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const nextRuleScope = event.target.value;
    setRuleScope(nextRuleScope === 'sameAccount' ? 'sameAccount' : 'all');
  };
  const saveButtonLabel = isMobile ? t('move', 'Move') : t('save', 'Save');

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='move-to-cash-flow-modal-title'
      >
        <Box sx={style}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography
              id='move-to-cash-flow-modal-title'
              variant='h6'
              component='h2'
            >
              {t('transactions.moveToCashFlow', 'Move to Cash Flow')}
            </Typography>
            <IconButton
              onClick={onClose}
              size='small'
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          {isMobile ? (
            <Typography variant='body1'>
              {t(
                'transactions.moveToCashFlowConfirmation',
                'Are you sure you want to move this transaction?',
              )}
            </Typography>
          ) : (
            <RadioGroup
              value={mode}
              onChange={handleModeChange}
            >
              <FormControlLabel
                value='moveOnly'
                control={<Radio />}
                label={t(
                  'transactions.moveOnlyThisTransaction',
                  'Move only this transaction',
                )}
              />
              <FormControlLabel
                value='createRule'
                control={<Radio />}
                label={t(
                  'transactions.createMoveRule',
                  'Create a rule to automatically move this and similar transactions in the future',
                )}
              />
            </RadioGroup>
          )}

          {!isMobile && mode === 'createRule' && (
            <>
              <Typography
                variant='body2'
                sx={{ mt: 1 }}
              >
                {t(
                  'transactions.alwaysMoveWhenDescription',
                  'Always move transactions when Description:',
                )}
              </Typography>
              <Stack
                direction='row'
                spacing={2}
                alignItems='center'
              >
                <Select
                  size='small'
                  value={comparisonOperator}
                  onChange={(e) => {
                    setComparisonOperator(e.target.value);
                  }}
                  sx={{ minWidth: 180 }}
                >
                  {comparisonOperators.map((op) => (
                    <MenuItem
                      key={op.value}
                      value={op.value}
                    >
                      {t(
                        `transactions.comparisonOperator.${String(op.value)}`,
                        op.defaultLabel,
                      )}
                    </MenuItem>
                  ))}
                </Select>
                {comparisonOperator === 1 ? (
                  <Typography
                    variant='body2'
                    sx={{ fontStyle: 'italic' }}
                  >
                    {transaction?.Description || ''}
                  </Typography>
                ) : (
                  <TextField
                    size='small'
                    fullWidth
                    value={comparisonText}
                    onChange={(e) => {
                      setComparisonText(e.target.value);
                    }}
                    placeholder={t(
                      'transactions.enterComparisonText',
                      'Enter comparison text',
                    )}
                  />
                )}
              </Stack>

              <RadioGroup
                sx={{ mt: 2 }}
                value={ruleScope}
                onChange={handleRuleScopeChange}
              >
                <FormControlLabel
                  value='sameAccount'
                  control={<Radio />}
                  label={t(
                    'transactions.applyRuleSameAccount',
                    'Apply rule only to transactions from the same account.',
                  )}
                  disabled={!hasWealthAccountId}
                />
                <FormControlLabel
                  value='all'
                  control={<Radio />}
                  label={t(
                    'transactions.applyRuleAllAccounts',
                    'Apply rule to all transactions, regardless of account.',
                  )}
                />
              </RadioGroup>
            </>
          )}

          <Stack
            direction='row'
            spacing={1}
            justifyContent='flex-end'
            sx={{ mt: 2 }}
          >
            <Button
              size='large'
              variant='outlined'
              onClick={onClose}
            >
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              size='large'
              variant='contained'
              onClick={() => {
                void handleSave();
              }}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={24} /> : saveButtonLabel}
            </Button>
          </Stack>
        </Box>
      </Modal>

      {snackbar && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default MoveToCashFlowModal;
